import {Card} from 'antd';
import {RadioChangeEvent} from 'antd/es/radio';
import React, {
    useEffect, useState, FC,
} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
    loadMetadata, selectMetadata, selectTableEntityMetadata, ISubsectionMenu, ITableEntityMeta,
} from 'entities/metadata';
import {loadTableData as loadTableDataAction} from 'modules/data';
import {
    filterNonRenderableFieldsAndUpdateState,
    initializeMetadataAndData,
    setSelectedSubsection,
} from 'modules/data/data-actions';
import {selectFilterEntityData, selectSelectedSubsection, selectTableEntityData} from 'modules/data/data-selectors';
import {IPageTemplateProps} from 'pages';
import {EEntityType} from 'shared/constants/entities';
import {useAppSelector} from 'store/config/hooks';
import {loadAcceptableExtensions} from 'store/slices/documents-uploading-slice';

import {Spinner} from '../../spinner';
import {Table} from '../table';
import {TableActions} from '../table-actions/table-actions';

export interface ISubsectionTableComponentProps extends Pick<IPageTemplateProps, 'entityName' | 'url' | 'hideTitle'> {
    entityName: string;
    entityType?: EEntityType;
}

export const SubsectionTable: FC<ISubsectionTableComponentProps> = ({
    entityName,
    url,
    entityType,
    hideTitle,
}: ISubsectionTableComponentProps) => {
    const dispatch = useDispatch();
    const [fieldsFiltered, setFieldsFiltered] = useState<boolean>(false);
    const metadata = useAppSelector(
        selectMetadata(entityName, entityType || EEntityType.SUBSECTION_TABLE),
    );
    const storeSubsection = useSelector(selectSelectedSubsection);
    const [subsection, setSubsection] = useState<ISubsectionMenu | undefined>(
        metadata?.subsections?.slice(0, 1).shift(),
    );
    const metadataSubsections = useAppSelector(selectTableEntityMetadata(subsection?.key || ''));
    const filterSubsections = useAppSelector(selectFilterEntityData(subsection?.key || ''));
    const dataSubsections = useAppSelector(selectTableEntityData(entityName));

    const tableDataOptions = {
        useContext: metadataSubsections?.useContext,
        useSearch: metadataSubsections?.isSearchable,
        disabledPagination: metadataSubsections?.disabledPagination,
    };

    useEffect(() => {
        const shouldLoadMetadata = !metadata;
        const shouldLoadSubsection = metadata && metadata?.subsections && !subsection && !storeSubsection;
        const shouldLoadMetadataSubsection = !metadataSubsections && subsection;
        const shouldLoadData = fieldsFiltered && (!dataSubsections || !dataSubsections?.rows);
        const shouldFilterData = metadataSubsections?.isFilterable && !filterSubsections?.data;
        const shouldLoadFilteredData = shouldLoadData && metadataSubsections?.isFilterable && filterSubsections?.data;
        const shouldLoadAllData = shouldLoadData && !metadataSubsections?.isFilterable;

        if (storeSubsection) {
            setSubsection(metadata?.subsections?.find(sub => sub.key === storeSubsection.selectedSubsection));
        }
        if (shouldLoadMetadata) {
            dispatch(loadMetadata(entityName, entityType || EEntityType.SUBSECTION_TABLE));
            return;
        }
        if (shouldLoadSubsection) {
            setSubsection(metadata?.subsections?.slice(0, 1).shift());
            return;
        }
        if (shouldLoadMetadataSubsection) {
            dispatch(loadMetadata(String(subsection?.key), EEntityType.TABLE));
            return;
        }
        if (shouldLoadFilteredData) {
            dispatch(loadTableDataAction(entityName, {
                ...tableDataOptions,
                paramsToBeConverted: filterSubsections?.data,
            }, `/settings.user.settings/${subsection?.key}`));
            return;
        }
        if (shouldLoadAllData) {
            dispatch(
                loadTableDataAction(entityName, {
                    ...tableDataOptions,
                    paramsToBeConverted: filterSubsections?.data,
                },
                `/settings.user.settings/${subsection?.key}`,
                metadataSubsections?.prefix),
            );
            return;
        }
        if (shouldFilterData) {
            dispatch(initializeMetadataAndData(EEntityType.FILTER)(entityName));
        }
    }, [metadataSubsections, filterSubsections, dataSubsections, metadata, subsection]);

    const isTableLoaded = metadataSubsections?.fields && dataSubsections?.rows;
    const cardTitle = !hideTitle && metadata?.title;

    useEffect(() => {
        if (metadataSubsections?.shouldLoadExtensions) {
            dispatch(loadAcceptableExtensions());
        }
    }, [metadataSubsections?.shouldLoadExtensions]);

    useEffect(() => {
        if (metadataSubsections?.fields && !fieldsFiltered) {
            dispatch(
                filterNonRenderableFieldsAndUpdateState(
                    metadataSubsections, metadataSubsections.name, EEntityType.TABLE, setFieldsFiltered,
                ),
            );
        }
    }, [metadataSubsections?.fields]);

    const handleSubsectionChange = ({target: {value}}: RadioChangeEvent) => {
        if (metadata.subsections && value) {
            setSubsection(metadata.subsections.find(sub => sub.key === value));
            dispatch(setSelectedSubsection({selectedSubsection: value}));
        }
    };

    return (
        <Card
            title={cardTitle}
        >
            <TableActions
                url={url}
                entityName={entityName}
                metadata={metadata}
                handleSubsectionChange={handleSubsectionChange}
                checkedSubsectionValue={subsection?.key}
            />
            {isTableLoaded ? (
                <Table
                    metadata={metadataSubsections as ITableEntityMeta}
                    tableData={dataSubsections}
                    url={`${subsection?.key}`}
                    entityName={entityName}
                    entityType={entityType || EEntityType.SUBSECTION_TABLE}
                />
            ) : <Spinner />}
        </Card>
    );
};
